import { useCallback } from 'react';
import { createSearchParams, useLocation } from 'react-router-dom';
import { useNavigate } from 'src/utils/useNavigate';
import routes from 'src/constants/routes';

type UseWithAuthoritiesType = (
  authorities: string[]
) => (action: () => Promise<any>, returnUrl?: string) => any;

const useWithAuthorities: UseWithAuthoritiesType = authorities => {
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(
    async (action, returnUrl) => {
      try {
        return await action();
      } catch (error) {
        const { status, type } = error as any;
        const searchParams = createSearchParams({
          returnUrl: returnUrl || `${location.pathname}${location.search}`,
        }).toString();

        // На бэкенде модель авторизации использует "пермишены" для определения прав доступа
        // к ресурсу. Набор "пермишенов" пользователя напрямую зависит от того,
        // какие пользовательские сценарии он прошёл для авторизации. На фронте для простоты
        // и удобства мы отталкиваемся от пользовательских сценариев. С точки зрения
        // пользовательских сценариев имеем три уровня прав:
        // 1) не залогинен по паролю - нельзя ни просматривать данные ни редактировать;
        // 2) залогинен по паролю - можно просматривать, но нельзя редакировать;
        // 3) прошёл двухфакторную авторизацию по номеру телефона - можно просматривать
        // и редактировать.
        // При этом, чтобы отличать ошибки доступа связанные с уровнем "1" (не залогинен)
        // и с уровень "2" (не прошёл двухфакторку), договорились с бэкендом, что
        // в первом случае будет использоваться статус код 401, а во втором 403.
        if (status === 403 || type === 'ACTION_IS_NOT_ALLOWED') {
          // Если пользователь залогинен по паролю и у него не хватает прав, значит
          // для повышения ему остаётся один сценарий: двухфакторная авторизация по телефону.
          return navigate({
            pathname: routes.phoneConfirm,
            search: searchParams,
          });
        }
        if (status === 401 || (status === 400 && type === 'USER_ID_NOT_SPECIFIED')) {
          // Такая ситуация возможна, если состояние сессии успело протухнуть к моменту
          // вызова экшна. В таком случае нужно залогиниться заново.
          return navigate({
            pathname: routes.authorization,
            search: searchParams,
          });
        }

        throw error;
      }
    },
    [location.pathname, location.search, navigate]
  );
};

export default useWithAuthorities;
